/**
 * Base HTTP client for the aidepo backend.
 *
 * No external deps — pure fetch.
 * - Sends the app session cookie (credentials: 'include') — аутентификация
 *   PWA идёт через cookie-сессию (вместо прежнего статического X-Api-Key).
 * - Sets Content-Type: application/json automatically for JSON bodies.
 * - Throws typed ApiError on non-2xx (401 — вход не выполнен).
 * - Returns parsed JSON on 2xx, undefined on 204.
 * - Configurable base URL via window.__AIDEPO_BASE_URL__ (default: same-origin).
 */

declare global {
  interface Window {
    __AIDEPO_BASE_URL__?: string;
  }
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  public readonly path: string;

  constructor(status: number, path: string, body: unknown, message?: string) {
    super(message ?? `API ${status} on ${path}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.path = path;
  }
}

function readBaseUrl(): string {
  if (typeof window !== 'undefined' && typeof window.__AIDEPO_BASE_URL__ === 'string') {
    return window.__AIDEPO_BASE_URL__;
  }
  return '';
}

function buildHeaders(extra?: Readonly<Record<string, string>>, hasJsonBody?: boolean): Headers {
  const h = new Headers();
  if (hasJsonBody === true) h.set('Content-Type', 'application/json');
  h.set('Accept', 'application/json');
  if (extra !== undefined) {
    for (const [k, v] of Object.entries(extra)) h.set(k, v);
  }
  return h;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function request(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  init?: RequestInit,
): Promise<unknown> {
  const url = readBaseUrl() + path;
  const hasJsonBody = body !== undefined && !(body instanceof FormData);
  const headers = buildHeaders(
    init?.headers as Record<string, string> | undefined,
    hasJsonBody,
  );

  // credentials: 'include' — отправляем cookie-сессию приложения.
  const fetchInit: RequestInit = { ...init, method, headers, credentials: 'include' };
  if (body !== undefined) {
    fetchInit.body = body instanceof FormData ? body : JSON.stringify(body);
  } else {
    delete fetchInit.body;
  }

  const res = await fetch(url, fetchInit);

  const parsed = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, path, parsed);
  }
  return parsed;
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  return (await request('GET', path, undefined, init)) as T;
}

export async function apiPost<T, B = unknown>(path: string, body?: B, init?: RequestInit): Promise<T> {
  return (await request('POST', path, body, init)) as T;
}

export async function apiPut<T, B = unknown>(path: string, body?: B, init?: RequestInit): Promise<T> {
  return (await request('PUT', path, body, init)) as T;
}

export async function apiPatch<T, B = unknown>(path: string, body?: B, init?: RequestInit): Promise<T> {
  return (await request('PATCH', path, body, init)) as T;
}

export async function apiDelete(path: string, init?: RequestInit): Promise<void> {
  await request('DELETE', path, undefined, init);
}

/** POST with multipart/form-data body. Content-Type header is left to the browser (boundary). */
export async function apiPostForm<T>(path: string, form: FormData, init?: RequestInit): Promise<T> {
  return (await request('POST', path, form, init)) as T;
}
